import { PortfolioWealthAllocation } from "@iliotech/generated-api-v3";
import { DRILL_DOWNS, getBackgroundColor } from "../../utils/CommonHelpers";
import { AllocationOption } from "../../reactHooks/context/StructureGrouping/types";
import { TimeSeriesChartItem } from "@iliotech/widgets";
import { ClickableLegendItem } from "@iliotech/component-library";

export interface BreakdownSeriesChartItem extends TimeSeriesChartItem {
  entries: {
    value: number;
    exposureValue: number;
    date: Date;
  }[];
}
export const mapAllocationDataToTimeSeriesItems = (
  data: PortfolioWealthAllocation[] | undefined,
  grouping: AllocationOption,
  currencyFormatter: (value: number) => string
) => {
  if (!data?.length) {
    return { timeSeriesData: [], legendItems: [] };
  }
  console.log({ data });
  const filterField = grouping.field;
  const filterLabel = grouping.labelField;

  const aggregateField = (DRILL_DOWNS[grouping.field]?.field ||
    filterField) as keyof PortfolioWealthAllocation;

  const aggregateLabel = (DRILL_DOWNS[grouping.field]?.labelField ||
    filterLabel) as keyof PortfolioWealthAllocation;

  // TODO change back end currencyCode to currencyLocalName
  let filteredItems =
    data?.map((item) => ({
      ...item,
      currencyLocalName: item.currencyCode,
    })) ?? [];

  const groupingKeys = new Map();

  filteredItems?.forEach((allocation: PortfolioWealthAllocation) => {
    groupingKeys.set(allocation[aggregateField], allocation[aggregateLabel]);
  });

  const toReturn: BreakdownSeriesChartItem[] = Array.from(groupingKeys).map(
    ([key, value]) => ({
      category: value as string,
      entries: filteredItems
        .filter((item) => item[aggregateField] === key)
        .map((entry) => ({
          value: entry.value,
          exposureValue: entry.exposureBase,
          date: new Date(entry.date),
        })),
      color: getColor(grouping, key as string),
    })
  );
  const timeSeriesData = toReturn.sort((a, b) =>
    a.category.localeCompare(b.category)
  );

  // Find the most recent date across all items
  const mostRecentDate = new Date(
    Math.max(...filteredItems.map((item) => new Date(item.date).getTime()))
  );

  // Filter items for the most recent date
  const mostRecentItems = filteredItems.filter(
    (item) => new Date(item.date).getTime() === mostRecentDate.getTime()
  );

  // Create a map to aggregate values by categories for only the most recent date
  const aggregatedLegendItems = new Map<
    string,
    { value: number; exposureBase: number }
  >();

  // Process only items from the most recent date
  mostRecentItems.forEach((item) => {
    const category = item[aggregateLabel] as string;
    if (!aggregatedLegendItems.has(category)) {
      aggregatedLegendItems.set(category, { value: 0, exposureBase: 0 });
    }
    const current = aggregatedLegendItems.get(category)!;
    current.value += item.value;
    current.exposureBase += item.exposureBase;
  });

  // Convert to final legendItems format
  const legendItems: (ClickableLegendItem & {
    exposureBase: string | number;
  })[] = Array.from(aggregatedLegendItems.entries()).map(
    ([category, totals]) => {
      const matchingItem = timeSeriesData.find(
        (item) => item.category === category
      );
      return {
        exposureBase: currencyFormatter(totals.exposureBase),
        value: currencyFormatter(totals.value),
        color: matchingItem?.color || "#000000",
        label: category,
      };
    }
  );

  return { timeSeriesData, legendItems };
};
function getColor(grouping: AllocationOption, categoryId: string) {
  return (
    grouping.getColor?.(categoryId) || getBackgroundColor(categoryId || "null")
  );
}
